// src/utils/validateRequiredFields.ts
export type RequiredField<K extends string = string> = { key: K; label: string };

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;

  // Strings: empty/whitespace is empty
  if (typeof value === 'string') return value.trim() === '';

  // Arrays: no items is empty
  if (Array.isArray(value)) return value.length === 0;

  // Objects: allow callers to decide; treat as present here
  // Numbers/booleans: treat as present (0/false are valid values)
  return false;
}

export type ValidationResult<K extends string = string> = {
  message: string;
  missingLabels: string[]; // for toast/snackbar or UI
  missingKeys: K[]; // for per-field highlights
  isValid: boolean;
};

export default function validateRequiredFields(
  formValues,
  requiredFields,
  opts?: { buildMessage?: (labels: string[]) => string },
) {
  const missingLabels = [];
  const missingKeys = [];

  requiredFields.forEach((field) => {
    if (isEmpty(formValues?.[field.key])) {
      missingLabels.push(field.label);
      missingKeys.push(field.key);
    }
  });

  const isValid = missingLabels.length === 0;
  const buildMessage =
    opts?.buildMessage ??
    ((labels: string[]) =>
      labels.length ? `Please complete the following required fields: ${labels.join(', ')}` : '');

  return {
    isValid,
    missingLabels,
    missingKeys,
    message: buildMessage(missingLabels),
  };
}
